import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import type { ClausulaCertificado } from '@/lib/certificados/plantillasPorDefecto';
import type { TipoCertificado } from './usePlantillasCertificados';

export const BUCKET_CERTIFICADOS = 'certificados-instalacion';

let bucketEnsurePromise: Promise<void> | null = null;

export interface CertificadoInstalacion {
  id: string;
  empresa_id: string;
  cliente_id: string;
  tipo: TipoCertificado;
  numero: number;
  numero_formateado: string;
  sede_id: string | null;
  sistemas_ids: string[];
  fecha_instalacion: string;
  descripcion_instalacion: string;
  referencia_proyecto: string | null;
  grado_intrusion: number | null;
  objeto: string;
  clausulas: ClausulaCertificado[];
  texto_legal: string;
  observaciones: string | null;
  estado: 'borrador' | 'pendiente_firma' | 'emitido' | 'anulado';
  firma_cliente: string | null;
  firma_empresa: string | null;
  nombre_firmante_cliente: string | null;
  fecha_firma: string | null;
  firma_origen: 'online' | 'manual' | null;
  firma_enviado_at: string | null;
  firma_email_destinatario: string | null;
  token_firma: string | null;
  token_firma_expira_at: string | null;
  pdf_path: string | null;
  archivado: boolean;
  fecha_anulacion: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface NuevoCertificadoInput {
  cliente_id: string;
  tipo: TipoCertificado;
  fecha_instalacion: string;
  sede_id?: string | null;
  sistemas_ids: string[];
  descripcion_instalacion?: string;
  referencia_proyecto?: string;
  grado_intrusion?: number | null;
  objeto: string;
  clausulas: ClausulaCertificado[];
  texto_legal: string;
  observaciones?: string;
  estado: 'borrador' | 'emitido';
  nombre_firmante_cliente?: string;
}

export function useCertificadosInstalacion(clienteId?: string) {
  const { perfil, user } = useAuth();
  const { toast } = useToast();
  const [certificados, setCertificados] = useState<CertificadoInstalacion[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!perfil?.empresa_id) return;
    setLoading(true);
    let q = (supabase as any)
      .from('certificados_instalacion')
      .select('*')
      .eq('empresa_id', perfil.empresa_id)
      .order('created_at', { ascending: false });
    if (clienteId) q = q.eq('cliente_id', clienteId);
    const { data, error } = await q;
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setCertificados((data ?? []) as CertificadoInstalacion[]);
    }
    setLoading(false);
  }, [perfil?.empresa_id, clienteId, toast]);

  useEffect(() => { load(); }, [load]);

  const asegurarBucket = useCallback(async () => {
    if (!user?.id) throw new Error('Sin sesión');
    if (!bucketEnsurePromise) {
      bucketEnsurePromise = supabase.functions
        .invoke('ensure-certificados-bucket', { body: {} })
        .then(({ error, data }) => {
          if (error) throw error;
          if ((data as any)?.error) throw new Error((data as any).error);
        })
        .catch((error) => {
          bucketEnsurePromise = null;
          throw error;
        });
    }
    await bucketEnsurePromise;
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !perfil?.empresa_id) return;
    asegurarBucket().catch((error) => {
      console.warn('No se pudo inicializar el bucket de certificados:', error);
    });
  }, [asegurarBucket, perfil?.empresa_id, user?.id]);

  const crearCertificado = async (input: NuevoCertificadoInput): Promise<CertificadoInstalacion> => {
    if (!perfil?.empresa_id || !user?.id) throw new Error('Sin sesión');

    const { data: numData, error: numErr } = await (supabase as any).rpc('generar_numero_certificado', {
      _empresa_id: perfil.empresa_id,
      _tipo: input.tipo,
    });
    if (numErr) throw numErr;
    const { numero, numero_formateado } = numData[0];

    const payload: any = {
      empresa_id: perfil.empresa_id,
      cliente_id: input.cliente_id,
      tipo: input.tipo,
      numero,
      numero_formateado,
      sede_id: input.sede_id || null,
      sistemas_ids: input.sistemas_ids,
      fecha_instalacion: input.fecha_instalacion,
      descripcion_instalacion: input.descripcion_instalacion || '',
      referencia_proyecto: input.referencia_proyecto || null,
      grado_intrusion: input.grado_intrusion ?? null,
      objeto: input.objeto,
      clausulas: input.clausulas,
      texto_legal: input.texto_legal,
      observaciones: input.observaciones || null,
      estado: input.estado,
      nombre_firmante_cliente: input.nombre_firmante_cliente || null,
      created_by: user.id,
    };

    const { data, error } = await (supabase as any)
      .from('certificados_instalacion')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;

    await load();
    return data as CertificadoInstalacion;
  };

  const actualizarCertificado = async (id: string, patch: Partial<CertificadoInstalacion>) => {
    const { error } = await (supabase as any)
      .from('certificados_instalacion')
      .update(patch)
      .eq('id', id);
    if (error) throw error;
    await load();
  };

  const eliminarCertificado = async (c: CertificadoInstalacion) => {
    if (c.pdf_path) {
      await asegurarBucket();
      const { error: removeError } = await supabase.storage.from(BUCKET_CERTIFICADOS).remove([c.pdf_path]);
      if (removeError) throw removeError;
    }
    const { error } = await (supabase as any)
      .from('certificados_instalacion')
      .delete()
      .eq('id', c.id);
    if (error) throw error;
    await load();
  };

  const subirPdf = async (c: CertificadoInstalacion, blob: Blob): Promise<string> => {
    if (!perfil?.empresa_id) throw new Error('Sin empresa');
    await asegurarBucket();
    const path = `${perfil.empresa_id}/${c.id}.pdf`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET_CERTIFICADOS)
      .upload(path, blob, { upsert: true, contentType: 'application/pdf' });
    if (upErr) {
      toast({ title: 'Error al subir PDF', description: upErr.message, variant: 'destructive' });
      throw upErr;
    }
    const { error: updateError } = await (supabase as any)
      .from('certificados_instalacion')
      .update({ pdf_path: path })
      .eq('id', c.id);
    if (updateError) throw updateError;
    await load();
    return path;
  };

  const descargarPdf = async (c: CertificadoInstalacion) => {
    if (!c.pdf_path) {
      toast({ title: 'Sin PDF', description: 'Este certificado aún no tiene PDF generado.' });
      return;
    }
    try {
      await asegurarBucket();
      const { data, error } = await supabase.storage
        .from(BUCKET_CERTIFICADOS)
        .createSignedUrl(c.pdf_path, 3600, { download: `${c.numero_formateado}.pdf` });
      if (error || !data) throw error ?? new Error('No se pudo generar el enlace de descarga');
      window.open(data.signedUrl, '_blank');
    } catch (error: any) {
      toast({ title: 'Error al descargar PDF', description: error?.message, variant: 'destructive' });
    }
  };

  const verPdf = async (c: CertificadoInstalacion) => {
    if (!c.pdf_path) {
      toast({ title: 'Sin PDF', description: 'Este certificado aún no tiene PDF generado. Pulsa "Regenerar PDF".' });
      return;
    }
    try {
      await asegurarBucket();
      const { data, error } = await supabase.storage
        .from(BUCKET_CERTIFICADOS)
        .createSignedUrl(c.pdf_path, 3600);
      if (error || !data) throw error ?? new Error('No se pudo generar el enlace de visualización');
      window.open(data.signedUrl, '_blank');
    } catch (error: any) {
      toast({ title: 'Error al abrir PDF', description: error?.message, variant: 'destructive' });
    }
  };

  const compartirPdf = async (c: CertificadoInstalacion) => {
    if (!c.pdf_path) {
      toast({ title: 'Sin PDF', description: 'Genera primero el PDF con "Regenerar PDF".' });
      return;
    }
    try {
      await asegurarBucket();
      const { data, error } = await supabase.storage
        .from(BUCKET_CERTIFICADOS)
        .createSignedUrl(c.pdf_path, 60 * 60 * 24 * 7);
      if (error || !data) throw error ?? new Error('No se pudo generar el enlace para compartir');
      const url = data.signedUrl;
      if (typeof navigator !== 'undefined' && (navigator as any).share) {
        await (navigator as any).share({
          title: `Certificado ${c.numero_formateado}`,
          text: `Certificado de instalación ${c.numero_formateado}`,
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast({ title: 'Enlace copiado', description: 'Válido durante 7 días.' });
    } catch (error: any) {
      toast({ title: 'Error al compartir PDF', description: error?.message, variant: 'destructive' });
    }
  };

  const generarLinkFirma = async (
    c: CertificadoInstalacion,
    opciones: { email?: string | null; diasValidez?: number } = {},
  ): Promise<{ url: string; token: string; expira_at: string }> => {
    const { data, error } = await supabase.functions.invoke('generar-link-firma-certificado', {
      body: {
        certificado_id: c.id,
        email: opciones.email ?? null,
        dias_validez: opciones.diasValidez ?? 30,
      },
    });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    await load();
    return data as { url: string; token: string; expira_at: string };
  };

  const subirCertificadoFirmado = async (c: CertificadoInstalacion, file: File): Promise<void> => {
    if (!perfil?.empresa_id) throw new Error('Sin empresa');
    await asegurarBucket();
    const path = `${perfil.empresa_id}/${c.id}.pdf`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET_CERTIFICADOS)
      .upload(path, file, { upsert: true, contentType: file.type || 'application/pdf' });
    if (upErr) throw upErr;

    const { error: updErr } = await (supabase as any)
      .from('certificados_instalacion')
      .update({
        pdf_path: path,
        estado: 'emitido',
        firma_origen: 'manual',
        fecha_firma: new Date().toISOString(),
        token_firma: null,
        token_firma_expira_at: null,
      })
      .eq('id', c.id);
    if (updErr) throw updErr;

    await load();
  };

  return {
    certificados,
    loading,
    reload: load,
    crearCertificado,
    actualizarCertificado,
    eliminarCertificado,
    subirPdf,
    descargarPdf,
    verPdf,
    compartirPdf,
    generarLinkFirma,
    subirCertificadoFirmado,
  };
}
